import type { Integration } from '@/services/db/integration/entity';
import { IntegrationType, warehouseCatalogPageSegment } from '@/features/integrations';
import { isEmptyString } from '@/utils/string';
import { generatePath } from '@/services/router/generate-path';

/**
 * What the "Get started" button does for one catalog entry.
 *
 * - `pipelines` — a cross-app deep link into the Pipelines (CDP) app.
 * - `journeys` — a page the embedded Journeys app still serves.
 * - `hydra` — a page this app serves.
 * - `external` — the partner's own site, in a new tab.
 * - `unavailable` — no button; see {@link resolveCta}.
 */
export type ExternalIntegrationCta =
  | { kind: 'pipelines' | 'journeys' | 'hydra' | 'external'; href: string; label: string }
  | { kind: 'unavailable' };

const GET_STARTED = 'Get started';

/**
 * The catalog fields the button is built from. `cta_url` is optional because
 * the catalog entity validates only `id` — an entry can arrive without a
 * destination, which the branches below read as having none.
 */
export type CtaFields = Pick<Integration, 'id' | 'name' | 'category' | 'type'> & { cta_url?: Integration['cta_url'] };

/**
 * Who is asking, for the intake form an `Experiment` integration points at. The
 * form prefills from these and attributes the submission to the account, so a
 * missing value is dropped rather than sent empty.
 */
export type ExperimentAttribution = {
  accountEmail: string | undefined;
  accountId: string | number | undefined;
  userId: string | number | undefined;
};

/**
 * The catalog's `cta_url` is usually a URL, but a handful of entries carry an
 * Ember route name instead, which has to be resolved to the URL the embedded app
 * serves. One is left in the catalog today:
 *
 * - `integrations.forms.general.index` (Netlify Forms) → the forms settings page.
 *
 * `integrations.type.warehouse_sync` used to be here as well, resolving to an
 * Ember route with no template behind it — see {@link warehouseCta}. It carried
 * the only `:type` segment any of these templates had, so the paths are now
 * literal; a future entry needing a dynamic segment has to fill it again.
 */
const JOURNEYS_ROUTE_PATHS = new Map([['integrations.forms.general.index', 'integrations/forms/general']]);

/** The `cta_url` the six data-warehouse catalog rows carry. */
const WAREHOUSE_SYNC_ROUTE_NAME = 'integrations.type.warehouse_sync';

/** Salesforce is a Pipelines-native source, so its button goes there rather than to a detail page. */
const SALESFORCE = 'Salesforce';
const SALESFORCE_PATH = '/pipelines/sources/add?source=salesforce';

/**
 * An `Experiment` integration has no page to send anyone to — its cta_url is an
 * intake form, and the query string is what tells us who asked and about what.
 *
 * Empty values are dropped rather than sent blank, and the whole thing is skipped
 * when nothing survives, so the form never receives `?email_address=`.
 */
function experimentUrl(integration: CtaFields, attribution: ExperimentAttribution): string {
  const params: [key: string, value: string | number | undefined][] = [
    ['email_address', attribution.accountEmail],
    ['account_id', attribution.accountId],
    ['user_id', attribution.userId],
    ['integration_type', integration.name],
    ['utm_source', 'In-App'],
    ['utm_content', integration.category],
    ['utm_term', integration.name],
    ['utm_campaign', 'Existing'],
  ];

  const query = params
    .filter(([, value]) => value != null && String(value) !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
    .join('&');

  const base = integration.cta_url ?? '';
  return query === '' ? base : `${base}?${query}`;
}

function isUrlPath(ctaUrl: string): boolean {
  return ctaUrl.startsWith('/');
}

function isAbsoluteUrl(ctaUrl: string): boolean {
  return /^https?:\/\//i.test(ctaUrl);
}

function pipelines(workspaceId: string, path: string): ExternalIntegrationCta {
  // oxlint-disable-next-line local-rules/enforce-generate-path -- cross-app Pipelines (CDP) deep-link, not a Hydra router path
  return { kind: 'pipelines', href: `/workspaces/${workspaceId}${path}`, label: GET_STARTED };
}

/**
 * "Get started" for a data-warehouse catalog row.
 *
 * These six rows carry `integrations.type.warehouse_sync`, which resolved to
 * `integrations/:type/warehouse_sync` — a route registered in the Ember router
 * with no template behind it, so the button landed on an empty page (FPS-3608).
 * Only Azure Blob Storage reached the API; the other five are seeded with a
 * non-production status.
 *
 * The destination is not a guess. The directory already replaces these rows with
 * its own cards, and those point at the same warehouse page per integration — so
 * this sends the reader where the card for the same integration sends them. The
 * two build their hrefs separately though, and only the id list is shared; see
 * {@link warehouseCatalogPageSegment}, which also records the plan-gate
 * asymmetry between the six.
 *
 * `unavailable` covers a row carrying this `cta_url` that is not one of the six,
 * which no button is better than a second empty page.
 */
function warehouseCta(workspaceId: string, integration: CtaFields): ExternalIntegrationCta {
  const type = warehouseCatalogPageSegment(integration.id);

  if (type == null) {
    return { kind: 'unavailable' };
  }

  // The three connector landing pages are literal children of this route, and
  // TanStack matches a literal before `$type`, so one typed build covers all six.
  const href = generatePath('/workspaces/:workspaceId/integrations/warehouse/:type', { workspaceId, type });

  return { kind: 'hydra', href, label: GET_STARTED };
}

function journeysRoute(workspaceId: string, integration: CtaFields): ExternalIntegrationCta {
  const path = JOURNEYS_ROUTE_PATHS.get(integration.cta_url ?? '');

  if (path == null) {
    return { kind: 'unavailable' };
  }

  // oxlint-disable-next-line local-rules/enforce-generate-path -- embedded Journeys-app deep-link, not a Hydra router path
  return { kind: 'journeys', href: `/workspaces/${workspaceId}/journeys/${path}`, label: GET_STARTED };
}

/**
 * The button for one catalog entry, from its type and `cta_url`.
 *
 * `unavailable` covers a `cta_url` that is neither a URL nor a route name we can
 * resolve — impossible with today's catalog, and better than a button that goes
 * nowhere.
 */
export function resolveCta(
  integration: CtaFields,
  workspaceId: string,
  attribution: ExperimentAttribution,
): ExternalIntegrationCta {
  const { type } = integration;
  // Resolved once for every branch below, as Ember's `ctaHref` getter was. The
  // catalog entity parses loosely — only `id` is validated — so an entry that
  // arrives without a destination is treated as having none rather than throwing
  // when a branch inspects the string.
  const destination = isEmptyString(integration.cta_url) ? '' : integration.cta_url;
  const ctaUrl = type === IntegrationType.experiment ? experimentUrl(integration, attribution) : destination;

  if (type === IntegrationType.pipelinesSource || type === IntegrationType.pipelinesDestination) {
    return isUrlPath(ctaUrl) ? pipelines(workspaceId, ctaUrl) : { kind: 'unavailable' };
  }

  if (type === IntegrationType.native) {
    if (integration.name === SALESFORCE) {
      return pipelines(workspaceId, SALESFORCE_PATH);
    }
    if (ctaUrl === WAREHOUSE_SYNC_ROUTE_NAME) {
      return warehouseCta(workspaceId, integration);
    }
    return isAbsoluteUrl(ctaUrl)
      ? { kind: 'external', href: ctaUrl, label: GET_STARTED }
      : journeysRoute(workspaceId, integration);
  }

  if (!isAbsoluteUrl(ctaUrl)) {
    return { kind: 'unavailable' };
  }

  return {
    kind: 'external',
    href: ctaUrl,
    label: type === IntegrationType.zapier ? 'Connect via Zapier' : GET_STARTED,
  };
}
